import { EventEmitter, Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import { SelectItem } from 'primeng/api';
import { BehaviorSubject } from 'rxjs';
import { first, tap } from 'rxjs/operators';
import { LazyLoadedWidgets } from 'src/app/app-routing-lazy';
import { LabelValueLower } from 'src/app/models/keyvalue';
import { ILazyComponentHandler, ISmartActionContext, ISmartActionMetadata, SmartActionContext, SmartActionContextItem } from 'src/app/models/smart-actions.models';
import { Tenant } from 'src/app/models/tenant';
import { PermissionType } from 'src/app/models/user';
import {
    IMemberListItemContext,
    IMemberListItemNew,
    IMemberSearchMetadataRepositoryMapping,
    IMemberSearchMetadataRequest,
    IMemberSearchMetadataResponse,
    IMemberSearchMetadataResponseFilter,
    IMemberSearchMetadataResponseFilterValue,
    IMemberSearchRequest,
    IMemberSearchResponse,
    IMemberSearchResponseFacetResult,
    IMemberSearchResponseRepositoryConfigurationSearchAction,
    IMemberSearchState,
    MemberActionConfig
} from 'src/app/pages/_searchs/member/member-search.model';
import { MemberListSaveFilterAction, MemberListState, MemberListClearFilterAction  } from 'src/app/services/member/member-list.state.service';
import { TelephonyQueryParamsService } from 'src/app/services/telephony/telephony-queryparams.service';
import { TelephonyQueryParams } from 'src/app/services/telephony/telephony-queryparams.state.models';
import { TenantService } from 'src/app/services/tenant/tenant.service';
import { GridFilterItem, GridFilterItemFinal, GridFilterProperty, GridFilterPropertyOperator, IFacetQueryItem } from 'src/app/shared/grid/grid.model';
import { AlertService, MessageSeverity } from '../alert.service';
import { UserPermissionService } from '../user/user-permission.service';
import { MemberEndpoint } from './member-endpoint.service';
import { ConfigsService } from '../configs/configs.service';

@Injectable({
    providedIn: 'root'
})
export class MemberSearchService {

    search(req: IMemberSearchRequest) {
       
        this.saveFiltering();
         let reqCopy = { ...req };
        reqCopy.criteria = this.formatPhoneNumberCriteria();
        reqCopy.criteria = this.escapeLeadingHyphen(reqCopy.criteria || '');
        
        return this.memberEndpoint.getSearch(reqCopy).pipe(tap(response => {
            this.setMetadataFilterValuesFromResult(response);
            this.formatMetadataFilters();

            return response;
        }))

    }
    
    private  formatPhoneNumberCriteria(): string {
        if (this.criteria) {            
            const phonePattern = /^"?\((\d{3})\)[\s-]?(\d{3})[\s-](\d{4})"?$/;
            const match = this.criteria.match(phonePattern);
            if (match) {
                const digits = match[1] + match[2] + match[3];
                return `Phones/Number:${digits}`;
            }
        }
        return this.criteria;
    }
        
    private escapeLeadingHyphen(str) {
        return str.replace(/^-/, '\\-');
    }    

    async updateFacets(req: IMemberSearchRequest, isEditing: boolean = true): Promise<void> {
        try {
            const response = await this.memberEndpoint.getFacets(req).toPromise();
            this.setMetadataFilterValuesFromFacetsResult(response);
            await this.formatMetadataFilters(isEditing);
        } catch (error) {
            console.error("Error updating facets:", error);
        }
    }

    removeColumnFilters(filterItem: GridFilterItem) {
        const memberSearchState = this.store.selectSnapshot<IMemberSearchState>(MemberListState.currentFilter);
        const itemIndex = memberSearchState.columnFilters?.findIndex(filter => filter.property === filterItem.property);
        if (itemIndex >= 0) {
            memberSearchState.columnFilters.splice(itemIndex, 1);
        }
    }

    private _selectedItem: IMemberListItemContext;

    get selectedItem() {
        return this._selectedItem;
    }

    selectedItem$ = new EventEmitter<IMemberListItemContext>();

    static processMemberSearchError(error: any) {

        let result: string = null;
        console.log(`error && error.error => `, error && error.error);

        if (error && error.error) {
            const errorMessage = error.error as string;
            const breakChar = errorMessage.indexOf('\n');
            if (breakChar >= 0) {
                result = errorMessage.substring(0, breakChar);
            }
        }

        if (!result || result.trim().length === 0) {
            result = `Error searching members. Please, check the query and try again`;
        }

        return result;
    }


    //#region Metadata
    metadataResponse: IMemberSearchMetadataResponse;
    metadataFilters: IMemberSearchMetadataResponseFilter[];
    gridFilterProperties: GridFilterProperty[] = [];
    gridFilters: GridFilterItem[] = [];

    // #region criteria
    private _criteria$ = new BehaviorSubject<string>(null);
    get criteria$() {
        return this._criteria$.asObservable();
    }
    get criteria() {
        return this._criteria$.value;
    }
    set criteria(value: string) {
        this._criteria$.next(value);
    }
    //#endregion

    tenants: Tenant[];
    filterInitialized: boolean;
    get filteringCriteriaExists() {
        return (
            (this.criteria && this.criteria.trim().length > 0) ||
            (this.gridFilters && this.gridFilters.length > 0 && this.gridFilters.some(filterItem => !!filterItem.value))
        );
    }

    //#endregion
    constructor(
        private alertService: AlertService,
        private telephonyParamsService: TelephonyQueryParamsService,
        private memberEndpoint: MemberEndpoint,
        private tenantService: TenantService,
        private store: Store,
        private userPermissionService: UserPermissionService,
        private configService: ConfigsService,

    ) {
    }
    //#region Metadata
    async getMetadata(): Promise<void> {
        const telephonyParams = this.telephonyParamsService.queryParamsSync || {} as TelephonyQueryParams;

        if( telephonyParams.criteria ){
            this.clearGlobalSearch();
            await this.store.dispatch(new MemberListClearFilterAction()).toPromise();
        }

        const criteria = telephonyParams.criteria || this.criteria;

        const req = {
            criteria: criteria,
            skillName: telephonyParams.skillName,
            contactId: telephonyParams.contactId,
            direction: telephonyParams.direction,
            tenantId: telephonyParams.tenantId
        } as IMemberSearchMetadataRequest;

        await this.userPermissionService.registerPermissions([{ selectionName: 'UI.MemberSearch.Filters', permissionType: PermissionType.namespace }], [/UI.MemberSearch.Filters/]);

        this.metadataResponse = await this.memberEndpoint.getSearchMetadata(req).pipe(first()).toPromise();
        this.criteria = criteria;

        await this.readFiltersFromMetadataResponse(this.metadataResponse);
        this.initializeGridFilters();
        await this.formatMetadataFilters();
    }

    setMetadataFilterValuesFromResult(response: IMemberSearchResponse) {
        this.metadataFilters.forEach(metadataFilter => {

            if (['TENANTID', 'REPOSITORY'].some(tenantOrRepo => tenantOrRepo === metadataFilter.property.toUpperCase())) {
                return;
            } else {
                const facetItem = response.facets.find(responseFacetItem => responseFacetItem.name.toUpperCase() === metadataFilter.property.toUpperCase());
                if (facetItem && facetItem.values) {
                    metadataFilter.values = facetItem.values.map(facetItemValue => ({ name: facetItemValue.value, value: facetItemValue.count } as IMemberSearchMetadataResponseFilterValue));
                } else {
                    if (metadataFilter.isFacetable) {
                        metadataFilter.values = [];
                    } else {
                        metadataFilter.values = null;
                    }
                }
            }

        });
    }

    setMetadataFilterValuesFromFacetsResult(response: IMemberSearchResponseFacetResult[] ) {
        this.metadataFilters.forEach(metadataFilter => {

            if (['TENANTID', 'REPOSITORY'].some(tenantOrRepo => tenantOrRepo === metadataFilter.property.toUpperCase())) {
                return;
            } else {
                const facetItem = response.find(responseFacetItem => responseFacetItem.name.toUpperCase() === metadataFilter.property.toUpperCase());
                if (facetItem && facetItem.values) {
                    metadataFilter.values = facetItem.values.map(facetItemValue => ({ name: facetItemValue.value, value: facetItemValue.count } as IMemberSearchMetadataResponseFilterValue));
                } else {
                    if (metadataFilter.isFacetable) {
                        metadataFilter.values = [];
                    } else {
                        metadataFilter.values = null;
                    }
                }
            }

        });
    }

    async formatMetadataFilters( isEditing: boolean = false) {

        let selectedTenantNames: string[];
        let selectedRepositoryNames: string[] = [];
        const appliedFilterForTenants = this.gridFilters.find(appliedFilter => appliedFilter.property?.name.toUpperCase() === 'TENANTID');
        if (!!appliedFilterForTenants) {
            if (Array.isArray(appliedFilterForTenants.value)) {
                const selectedTenants = (appliedFilterForTenants.value as { label: string, value: string }[]);
                selectedTenantNames = selectedTenants.map(selected => selected.value);
            }
        }

        const appliedFilterForRepositories = this.gridFilters.find(appliedFilter => appliedFilter.property?.name.toUpperCase() === 'REPOSITORY');
        if (!!appliedFilterForRepositories) {
            if (Array.isArray(appliedFilterForRepositories.value)) {
                const selectedRepositories = (appliedFilterForRepositories.value as { label: string, value: string }[]);
                selectedRepositoryNames = selectedRepositories.map(selected => selected.value);
            }
        }

        this.gridFilterProperties.forEach(async filterPropertyItem => {

            /******************************Custom Repository Filter Value ******************/
            const activeRepositories: string[] = [];
            let selectedTenantsMapping: IMemberSearchMetadataRepositoryMapping[] = [];
            const metadataFilterForPropertyItem = this.metadataFilters.find(metaFilter => metaFilter.property.toUpperCase() === filterPropertyItem.name.toUpperCase());

            //format tenant id filter
            if (filterPropertyItem.name.toUpperCase() === 'TENANTID') {
                filterPropertyItem.value = (metadataFilterForPropertyItem.values || [])
                    .filter(t => this.tenantService.findTenantByIdSync(t.name) != null)
                    .map(propertyItemValue => ({
                        label: (this.tenantService.findTenantByIdSync(propertyItemValue.name)).displayName,
                        value: propertyItemValue.name,
                        count: propertyItemValue.count
                    } as LabelValueLower));
                return;
            }

            if (!metadataFilterForPropertyItem.isFacetable) {
                return;
            }

            if (metadataFilterForPropertyItem && metadataFilterForPropertyItem.mappings && metadataFilterForPropertyItem.mappings.length) {

                // Map to tenant IDs
                if (selectedTenantNames && selectedTenantNames.length) {
                    selectedTenantsMapping = metadataFilterForPropertyItem.mappings.filter(item => selectedTenantNames.indexOf(item.tenantId) >= 0);
                }

                if (filterPropertyItem.name.toUpperCase() === 'REPOSITORY') {  //Format repositories for selected tenants
                    selectedTenantsMapping.map(tenant => tenant.repositories).forEach(selectedTenantRepositoryMapping => {
                        // include all repositories in the selected tenants that are not already included in the active repositories
                        const toInclude = selectedTenantRepositoryMapping.map(item => item.name).filter(item => activeRepositories.indexOf(item))
                        activeRepositories.push(...toInclude);
                    });

                    filterPropertyItem.value = activeRepositories.map(repository => ({ label: repository, value: repository }));
                } else {

                    const showFilter = (selectedTenantsMapping.length === 0) || selectedTenantsMapping.some(selectedTenantMapping => {

                        if (selectedTenantMapping.repositories && selectedTenantMapping.repositories.length && selectedRepositoryNames.length > 0) {
                            return selectedTenantMapping.repositories.some(selectedTenantRepo => selectedRepositoryNames.some(selectedRepoName => selectedRepoName === selectedTenantRepo.name));
                        } else {
                            // If not repository inside the selected tenant assume the exixtence of the maching tenant as the filter is available
                            return true;
                        }
                    });

                    if (showFilter) {
                        filterPropertyItem.value = (metadataFilterForPropertyItem.values || []).map(propertyItemValue => ({ label: propertyItemValue.name, value: propertyItemValue.name, count: propertyItemValue.count } as LabelValueLower));
                    } else {
                        filterPropertyItem.value = [];
                    }
                }

                //applied filters
                const memberSearchState = this.store.selectSnapshot<IMemberSearchState>(MemberListState.currentFilter);
                //remove if already displayed
                if(!isEditing) {
                    this.gridFilterProperties = this.gridFilterProperties.filter(property => {
                        return !memberSearchState.columnFilters?.some(appliedFilter => appliedFilter.property?.name === property.name);
                    });
                }

                const indexForAppliedPropertyItemFilter = this.gridFilters.findIndex(appliedFilter => appliedFilter.property?.name.toUpperCase() === filterPropertyItem.name.toUpperCase());

                    if (indexForAppliedPropertyItemFilter >= 0 ) {
                        if (!filterPropertyItem.value || filterPropertyItem.value.length === 0) {
                            filterPropertyItem.value = null;
                            this.gridFilters.splice(indexForAppliedPropertyItemFilter, 1);
                            const b = 0;
                        } else if (this.gridFilters[indexForAppliedPropertyItemFilter]) {
                            // remove from selected repository filter, values that are not applied for current tenant selection
                            const value = this.gridFilters[indexForAppliedPropertyItemFilter].value as LabelValueLower[];
                            const gridGilterActive = value.filter(repoFilterItem => activeRepositories.length === 0 || activeRepositories.some(activeRepo => activeRepo === (repoFilterItem as { value: string }).value));

                            if (gridGilterActive.length > 0) {
                                this.gridFilters[indexForAppliedPropertyItemFilter].value = gridGilterActive;
                            } else {
                                // remove from gridFilter if not values left after clean the selected repositories
                                this.gridFilters.splice(indexForAppliedPropertyItemFilter, 1);
                            }
                        }
                    }
            } else {
                filterPropertyItem.value = (metadataFilterForPropertyItem.values || []).map(propertyItemValue => ({ label: propertyItemValue.name, value: propertyItemValue.name, count: propertyItemValue.count } as LabelValueLower));
            }

        });

    }

    //#region prepare for query
    formatSearchMemberFilters(): GridFilterItemFinal[] {
        const cloneGridFilter = this.gridFilters.slice(0).map(filterItem => new GridFilterItemFinal(filterItem));

        //#region Custom Format For Tenant
        let tenantFilterValues: string[];
        const indexForTenantFilter = cloneGridFilter.findIndex(filterItem => filterItem.property && filterItem.property.toUpperCase() === 'TENANTID');
        if (indexForTenantFilter >= 0) {
            const tenantElement = cloneGridFilter.splice(indexForTenantFilter, 1);
            tenantFilterValues = tenantElement[0].value.split('|').map(tenantItem => tenantItem.trim());

            const tenantFilters = tenantFilterValues.map(value => new GridFilterItemFinal('TenantId', {
                operator: '=',
                value,
                isCollection: false
            } as GridFilterItemFinal));
            cloneGridFilter.push(...tenantFilters);
        }

        //#endregion

        //#region Custom Format For DateOfBirth
        const indexForDOBFilter = cloneGridFilter.findIndex(filterItem => ['DATEOFBIRTH', 'DOB'].some(option => option === filterItem.property && filterItem.property.toUpperCase()));
        if (indexForDOBFilter >= 0) {
            cloneGridFilter[indexForDOBFilter].type = 'DateTime';
        }
        //#endregion


        return cloneGridFilter;
    }
    //#endregion

    saveFiltering() {
        const extraData = {
            criteria: this.criteria,
            columnFilters: [...this.gridFilters],
        } as IMemberSearchState;

        if (!!this.filterInitialized) {
            this.store.dispatch(new MemberListSaveFilterAction(extraData));
        }
    }


    clearGlobalSearch() {
        this.gridFilters.splice(0);
        this.criteria = null;

        this.saveFiltering();

    }

    //#endregion
    buildSmartActionContext(itemContext: IMemberListItemContext): ISmartActionContext {

        const groupActions = itemContext.item.Actions;
        const actions = (groupActions || []).map(actionItem => {
            return this.openComponentSmartAction(actionItem, itemContext);
        });

        return new SmartActionContext({
            id: `member-search`,
            displayName: 'Member Search Actions',
            description: 'Member search actions',
            actions
        } as ISmartActionContext);
    }

    suspendMemberSmartAction(item: IMemberListItemContext): SmartActionContextItem {
        return new SmartActionContextItem({
            id: `member-suspendcomponent`,
            displayName: 'Suspend',
            description: 'Suspend member',
            disabled: false,
            target: this,
            actionMetadata: {
                item
            } as MemberActionConfig,
            command: async (context: ISmartActionMetadata) => {
                const sourceAction = context.sourceAction as SmartActionContextItem;
                const handler = context.lazyComponentHandler as ILazyComponentHandler;
                const target = context.sourceAction.target as MemberSearchService;
                const config = sourceAction.actionMetadata as MemberActionConfig;

                try {
                    await handler.setCurrentCustomView(LazyLoadedWidgets.COMPONENT_MEMBERSUSPEND, config);
                } catch (error) {
                    target.alertService.showStickyMessage('Error', 'Error opening the component', MessageSeverity.error, error);
                } finally {
                }

            }
        } as SmartActionContextItem);
    }

    convertMemberSmartAction(action: IMemberSearchResponseRepositoryConfigurationSearchAction, item: IMemberListItemContext): SmartActionContextItem {
        return new SmartActionContextItem({
            id: `member-convert`,
            displayName: 'Convert',
            description: 'Convert to member',
            disabled: false,
            target: this,
            actionMetadata: {
                item,
                selectedAction: action
            } as MemberActionConfig,
            command: async (context: ISmartActionMetadata) => {
                const sourceAction = context.sourceAction as SmartActionContextItem;
                const handler = context.lazyComponentHandler as ILazyComponentHandler;
                const target = context.sourceAction.target;
                const config = sourceAction.actionMetadata as MemberActionConfig;

                try {
                    await handler.setCurrentCustomView(LazyLoadedWidgets.COMPONENT_MEMBERCONVERT, config);
                } catch (error) {
                    target.alertService.showStickyMessage('Error', 'Error retrieving facility request', MessageSeverity.error, error);
                } finally {
                    // target.afterSmartAction(listItem);
                }
            }
        } as SmartActionContextItem);
    }

    openComponentSmartAction(actionItem: IMemberSearchResponseRepositoryConfigurationSearchAction, item: IMemberListItemContext, label?: string): SmartActionContextItem {
        return new SmartActionContextItem({
            id: `member-opencomponent`,
            displayName: actionItem.DisplayName,
            description: actionItem.DisplayName,
            icon: actionItem.Icon && actionItem.Icon.trim().length > 0 ? actionItem.Icon : undefined,
            disabled: false,
            target: this,
            actionMetadata: {
                item,
                selectedAction: actionItem
            } as MemberActionConfig,
            command: async (context: ISmartActionMetadata) => {
                const sourceAction = context.sourceAction as SmartActionContextItem;
                const handler = context.lazyComponentHandler as ILazyComponentHandler;
                const target = context.sourceAction.target;
                const config = sourceAction.actionMetadata as MemberActionConfig;

                try {
                    await handler.setCurrentCustomView(config.selectedAction.UIComponent, config);
                } catch (error) {
                    target.alertService.showStickyMessage('Error', 'Error opening the component', MessageSeverity.error, error);
                } finally {
                }

            }
        } as SmartActionContextItem);
    }

    responsiblePartyComponentSmartAction(actionItem: IMemberSearchResponseRepositoryConfigurationSearchAction, item: IMemberListItemContext): SmartActionContextItem {
        return new SmartActionContextItem({
            id: `responsibleparty-opencomponent`,
            displayName: 'Responsible Party' || actionItem.DisplayName,
            description: actionItem.DisplayName,
            icon: actionItem.Icon && actionItem.Icon.trim().length > 0 ? actionItem.Icon : undefined,
            disabled: false,
            target: this,
            actionMetadata: {
                item,
                selectedAction: actionItem
            } as MemberActionConfig,
            command: async (context: ISmartActionMetadata) => {
                const sourceAction = context.sourceAction as SmartActionContextItem;
                const handler = context.lazyComponentHandler as ILazyComponentHandler;
                const target = context.sourceAction.target;
                const config = sourceAction.actionMetadata as MemberActionConfig;

                try {
                    await handler.setCurrentCustomView(LazyLoadedWidgets.COMPONENT_RESPONSIBLEPARTYOPEN, config);
                } catch (error) {
                    target.alertService.showStickyMessage('Error', 'Error opening the component', MessageSeverity.error, error);
                } finally {
                    // target.afterSmartAction(listItem);
                }

            }
        } as SmartActionContextItem);
    }

    openEndpointSmartAction(item: IMemberListItemNew): SmartActionContextItem {
        throw new Error('Method not implemented.');
    }

    /************************************Search Metadata******************************************* */
    async readFiltersFromMetadataResponse(res: IMemberSearchMetadataResponse) {

        let filtersToSkip = await this.getFiltersUserDoesNotHaveAccessTo();
        let gridFilterProperties = [];
        const metadataFilters = res.filters;
        let filterItem: GridFilterProperty;

        metadataFilters.forEach(filter => {
            if (filtersToSkip.some(filterNameToSkip => filterNameToSkip.toLocaleLowerCase() === filter.property.toLowerCase())) {
                return;
            }
            const operators = (filter.isFacetable ? ['equal to'] : ['equal to', 'not equal to', 'starts with']) as unknown as GridFilterPropertyOperator[];
            const displayComponent = (filter.isFacetable ? 'multiselect' : 'input');

            filterItem = new GridFilterProperty(filter.property, filter.property, operators, displayComponent, filter.property.includes('/'));

            filterItem.setValueFunc((() => {
                if (filter.isFacetable) {
                    if (filter.values) {
                        return (filter.values || []).map(val => ({
                            label: val.name,
                            value: val.name
                        } as SelectItem));
                    } else if (filter.mappings) {

                    }
                } else {
                    return filter.values;
                }
            }).bind(this));

            gridFilterProperties.push(filterItem);

        });

        this.gridFilterProperties = gridFilterProperties.sort((a, b) => {
            return a.displayName < b.displayName
                ? -1
                : (a.displayName > b.displayName ? 1 : 0);
        });

        this.metadataFilters = metadataFilters;
    }

    async getFiltersUserDoesNotHaveAccessTo(): Promise<string[]>{
        const configItem = await this.configService.firstOrDefault("MEMBER_SEARCH_PERMISSIONS", {});
        let filterPermissions: FilterPermission[] = [];

        try {
            const parsed = JSON.parse(configItem.value) as { filterPermissions?: FilterPermission[] };
            filterPermissions = parsed.filterPermissions ?? [];
        } catch {
            filterPermissions = [];
        }

        let filtersToSkip: string[] = [];

        filterPermissions.forEach(permission => {
            let hasPermission = this.userPermissionService.hasPermission(permission.permissionName);
            if (!hasPermission) {
                filtersToSkip.push(permission.filter);
            }
        });

        return filtersToSkip;
    }

    initializeGridFilters() {
        const telephonyParams = this.telephonyParamsService.queryParamsSync || {} as TelephonyQueryParams;
        const memberSearchState = this.store.selectSnapshot<IMemberSearchState>(MemberListState.currentFilter);
        const gridFilters: GridFilterItem[] = [];
        let criteria: string;
        let tenants: Tenant[] = [];

        if (!!memberSearchState) {
            if (memberSearchState.columnFilters && !telephonyParams.tenantId) {
                memberSearchState.columnFilters.filter(filterItem => !!filterItem.property).forEach(filterItem => {
                    const newFilter = new GridFilterItem(filterItem);
                    newFilter.property = this.gridFilterProperties.find(prop => prop.name === newFilter.property.name) || newFilter.property;
                    gridFilters.push(newFilter);
                });
            }
            else if(telephonyParams.tenantId ){
                const tenantFilter = new GridFilterItem();
                const gridFilerProperty = this.gridFilterProperties.find(prop => prop.name.toLocaleLowerCase() === "tenantid");
                tenantFilter.property = gridFilerProperty;
                tenantFilter.operator = 'equal to';
                tenantFilter.editing = false;
                tenantFilter.isNew = true;
                tenantFilter.value = (this.tenantService.findTenantByIdSync(telephonyParams.tenantId)) ? [{ label: this.tenantService.findTenantByIdSync(telephonyParams.tenantId).displayName, value: telephonyParams.tenantId.toUpperCase() }] : [];
                const propertyValue = this.metadataFilters.find(metaFilter => metaFilter.property.toUpperCase() === "TENANTID")?.values.find(val => val.name.toLocaleLowerCase() === telephonyParams.tenantId.toLowerCase());

                if (propertyValue) {
                    propertyValue.isSelected = true;
                }

                gridFilters.push(tenantFilter);
            }

            criteria = telephonyParams.criteria || memberSearchState.criteria;
            tenants = this.tenantService.activeTenantsSync.filter(globalTenant => memberSearchState.tenants && memberSearchState.tenants.some(selected => selected === globalTenant.tenantName));
        }

        this.gridFilters = gridFilters;

        this.criteria = criteria;
        this.tenants = tenants;
        this.filterInitialized = true;

    }

    getColumnFilterItem(gridFilerProperty: GridFilterProperty): GridFilterItem {
        const result = new GridFilterItem();
        result.property = gridFilerProperty;
        result.operator = 'equal to';
        result.editing = true;
        result.isNew = true;
        return result;
    }

    setSelectedItem(item: IMemberListItemContext) {
        this._selectedItem = item;
        this.selectedItem$.next(item);
    }

    removeGridFilters(filterItem: GridFilterItem) {
        const memberSearchState = this.store.selectSnapshot<IMemberSearchState>(MemberListState.currentFilter);
        const columnFilters = memberSearchState.columnFilters.filter(filter => filter.property?.name !== filterItem.property?.name);

        const extraData = {
            criteria: this.criteria,
            columnFilters: [...columnFilters],
        } as IMemberSearchState;

        this.store.dispatch(new MemberListSaveFilterAction(extraData));
    }

    getGridFilterProperties(columnFilters: GridFilterItemFinal[] = null, facets: string[]): GridFilterProperty[] {
        this.updateStateDependentFilters(columnFilters, facets);
        return this.gridFilterProperties;
    }

    updateStateDependentFilters(columnFilters: GridFilterItemFinal[] = null, facets: string[]): void {
        const stateDependentFilterNames = ["addresses/city", "addresses/postalcode", "addresses/county", "addresses/street1"];

        const stateFilter = columnFilters?.find(columnFilter => columnFilter?.property?.toLowerCase() === "addresses/state");
        const facetHasCity = facets?.some(facet => facet.toLowerCase() === "addresses/city");

        //facets
        if (stateFilter && !facetHasCity) {
            facets.push('Addresses/City');
        } else if (!stateFilter && facetHasCity) {
            facets = facets.filter(facet => facet.toLowerCase() !== "addresses/city");
        }

        //grid filters
        if (!stateFilter) {
            this.gridFilterProperties = this.gridFilterProperties.filter(filter => stateDependentFilterNames.some(name => filter.displayName?.toLowerCase() === name) === false);
        }

    }
}

export class FilterPermission {
    filter: string;
    permissionName: string;
}
